import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

export type RowAction = Readonly<{
  id: string;
  label: string;
  onSelect: () => void;
  /** Withheld until the read behind it has settled, or where it has nothing to act on. */
  disabled?: boolean;
  /** Why it is withheld. Read out with the entry rather than left to be guessed at. */
  disabledReason?: string;
}>;

type RowActionMenuProps = Readonly<{
  id: string;
  ariaLabel: string;
  actions: readonly RowAction[];
  /** Runs before the menu opens, so a read an entry waits on starts on the way in. */
  onOpen?: () => void;
}>;

export const RowActionMenu = ({ id, ariaLabel, actions, onOpen }: RowActionMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = anchor !== null;

  const close = () => setAnchor(null);

  return (
    <>
      <IconButton
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${id}-menu` : undefined}
        onClick={(event) => {
          event.stopPropagation();
          onOpen?.();
          setAnchor(event.currentTarget);
        }}
      >
        <AppIcon iconName="DotsVertical" diameter="20px" color="currentColor" />
      </IconButton>
      <Menu
        id={`${id}-menu`}
        anchorEl={anchor}
        open={isOpen}
        onClose={close}
        // The menu is portalled out of the row in the DOM but stays a React child of it, so
        // synthetic clicks — including the backdrop click that dismisses it — still reach the
        // row's own handler and fire whatever the row does on click.
        onClick={(event) => event.stopPropagation()}
        // A withheld entry still takes focus, so its reason is read out rather than skipped over.
        MenuListProps={{ 'aria-label': ariaLabel, disabledItemsFocusable: true }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            id={action.id}
            disabled={action.disabled}
            sx={menuItemSx}
            onClick={(event) => {
              event.stopPropagation();

              // A withheld entry is kept focusable so its reason is read out, which also leaves
              // it clickable: aria-disabled is not the same fence as a disabled button.
              if (action.disabled) return;

              close();
              action.onSelect();
            }}
          >
            <Box sx={entrySx}>
              <Typography variant="text-sm-medium">{action.label}</Typography>
              {action.disabled && action.disabledReason ? (
                <Typography id={`${action.id}-reason`} variant="text-xs-regular" sx={reasonSx}>
                  {action.disabledReason}
                </Typography>
              ) : null}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// The theme's MuiMenuItem override forces select options transparent on hover; action menus
// get a light highlight so the pointed-at entry is legible.
const menuItemSx: SxProps<Theme> = ({ backrs }) => ({
  '&.MuiMenuItem-root:hover, &.Mui-focusVisible': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-primary-hover']} !important`,
  },
});

const entrySx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const reasonSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'normal',
});
